// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Design System Components (D2 Evoluto)
// Porting da /design_preview/d2-shared.jsx.
// Dipendenze: tokens.js, icons.js. Zero librerie esterne.
// Convenzioni: inline-style (coerente con App.js esistente), no CSS-in-JS,
// no Tailwind. Componenti puri, presentation-only. Nessuna chiamata API,
// nessuna logica di business.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT, { catFor } from './tokens';
import I, { Ico } from './icons';

// ─────── Logo ─────────────────────────────────────────────────────────────────
// Asset serviti da /public/brand/ (loghi ufficiali LabThree del prodotto).
// Per i loghi azienda (mostrati al login e in header multi-tenant) si usa
// il campo `azienda.logo` servito dal backend — NON questi file.

export const LogoIcon = ({ size = 32, style }) => (
  <img
    src={`${process.env.PUBLIC_URL || ''}/brand/labthree-icona.svg`}
    width={size}
    height={size}
    alt="LabThree"
    style={{ display: 'block', ...style }}
  />
);

export const LogoOriz = ({ height = 40, style }) => (
  <img
    src={`${process.env.PUBLIC_URL || ''}/brand/labthree-logo.svg`}
    height={height}
    alt="LabThree"
    style={{ display: 'block', ...style }}
  />
);

// ─────── ProgressRing ─────────────────────────────────────────────────────────

export const ProgressRing = ({ size = 56, stroke = 5, value = 0, color = LT.blu, bg = LT.n200, children }) => {
  const v = Math.max(0, Math.min(1, Number(value) || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - v);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} stroke={bg} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={off}
          strokeLinecap="round"
        />
      </svg>
      {children && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: size < 60 ? 12 : 14,
            fontWeight: 700,
            color: LT.n800,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// ─────── Chip ────────────────────────────────────────────────────────────────

const CHIP_KINDS = {
  neutro:  { c: LT.n700,    bg: LT.n100 },
  blu:     { c: LT.blu,     bg: LT.bluSoft },
  verde:   { c: LT.verde,   bg: LT.verdeSoft },
  rosso:   { c: LT.rosso,   bg: LT.rossoSoft },
  arancio: { c: LT.arancio, bg: LT.arancioSoft },
  viola:   { c: LT.viola,   bg: LT.violaSoft },
  ciano:   { c: LT.ciano,   bg: LT.cianoSoft },
};

export const Chip = ({ kind = 'neutro', icon, children, soft = true, size = 'md', style }) => {
  const s = CHIP_KINDS[kind] || CHIP_KINDS.neutro;
  const pad = size === 'sm' ? '3px 8px' : '5px 10px';
  const fs = size === 'sm' ? 11 : 12;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: pad,
        borderRadius: 999,
        background: soft ? s.bg : s.c,
        color: soft ? s.c : '#fff',
        fontSize: fs,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon && <Ico d={I[icon]} size={size === 'sm' ? 11 : 13} />}
      {children}
    </span>
  );
};

// ─────── Card ────────────────────────────────────────────────────────────────

export const Card = ({ children, pad = 20, style, hover = false, onClick, as = 'div', className }) => {
  const Comp = as;
  return (
    <Comp
      onClick={onClick}
      className={className}
      style={{
        background: '#fff',
        borderRadius: LT.r.lg,
        boxShadow: LT.sh2,
        padding: pad,
        border: `1px solid ${LT.n150}`,
        transition: 'box-shadow .15s, transform .15s',
        ...(hover || onClick ? { cursor: 'pointer' } : null),
        ...style,
      }}
    >
      {children}
    </Comp>
  );
};

// ─────── Button ──────────────────────────────────────────────────────────────

const BTN_VARIANTS = {
  primary: { background: LT.grad, color: '#fff', boxShadow: '0 4px 12px rgba(37,99,235,.25)' },
  solid:   { background: LT.blu, color: '#fff' },
  ghost:   { background: 'transparent', color: LT.n700 },
  soft:    { background: LT.bluSoft, color: LT.blu },
  outline: { background: '#fff', color: LT.n700, border: `1px solid ${LT.n200}` },
  danger:  { background: LT.rossoSoft, color: LT.rosso },
  success: { background: LT.verde, color: '#fff' },
};

export const Btn = ({
  kind = 'primary',
  icon,
  iconRight,
  children,
  size = 'md',
  style,
  onClick,
  block,
  disabled,
  type = 'button',
  title,
}) => {
  const p = size === 'lg' ? '12px 20px' : size === 'sm' ? '6px 12px' : '9px 16px';
  const fs = size === 'lg' ? 15 : size === 'sm' ? 12 : 13;
  const base = {
    display: block ? 'flex' : 'inline-flex',
    width: block ? '100%' : 'auto',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    padding: p,
    borderRadius: LT.r.md,
    fontSize: fs,
    fontWeight: 600,
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    fontFamily: LT.sans,
    transition: 'all .15s',
    lineHeight: 1,
  };
  return (
    <button
      type={type}
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{ ...base, ...(BTN_VARIANTS[kind] || BTN_VARIANTS.primary), ...style }}
    >
      {icon && <Ico d={I[icon]} size={fs + 3} />}
      {children}
      {iconRight && <Ico d={I[iconRight]} size={fs + 3} />}
    </button>
  );
};

// ─────── StatoDot ────────────────────────────────────────────────────────────

const STATO_DOT_MAP = { ok: LT.verde, nc: LT.rosso, warn: LT.arancio, pending: LT.n400, info: LT.ciano };

export const StatoDot = ({ kind = 'pending', size = 8, style }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: 999,
      background: STATO_DOT_MAP[kind] || LT.n400,
      display: 'inline-block',
      flexShrink: 0,
      ...style,
    }}
  />
);

// ─────── CatTile (categoria HACCP a tile colorata) ───────────────────────────

export const CatTile = ({ cat, size = 36, style }) => {
  const s = catFor(cat);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: LT.r.md,
        background: s.bg,
        color: s.c,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...style,
      }}
    >
      <Ico d={I[s.icon] || I.list} size={Math.round(size * 0.5)} />
    </div>
  );
};

// ─────── Field (display di un valore etichettato, read-only) ─────────────────

export const Field = ({ label, value, hint, right, style }) => (
  <div style={style}>
    {label && (
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: LT.n500,
          textTransform: 'uppercase',
          letterSpacing: '.05em',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
    )}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px',
        background: '#fff',
        border: `1px solid ${LT.n200}`,
        borderRadius: LT.r.md,
        fontSize: 14,
        color: LT.n800,
      }}
    >
      <span style={{ flex: 1 }}>{value}</span>
      {right}
    </div>
    {hint && <div style={{ fontSize: 11, color: LT.n400, marginTop: 4 }}>{hint}</div>}
  </div>
);

// ─────── TopBar (shell desktop) ──────────────────────────────────────────────
// Lasciato con API minimal: titolo, utente, azienda + slot children per
// azioni centrali (search, filtri…). Avatar calcolato da iniziali.

function iniziali(nomeCompleto = '') {
  return String(nomeCompleto)
    .split(' ')
    .filter(Boolean)
    .map(x => x[0]?.toUpperCase() || '')
    .slice(0, 2)
    .join('');
}

export const TopBar = ({ titolo, utente, azienda, onBell, onProfilo, children }) => (
  <div
    style={{
      height: 60,
      display: 'flex',
      alignItems: 'center',
      padding: '0 24px',
      background: '#fff',
      borderBottom: `1px solid ${LT.n150}`,
      flexShrink: 0,
    }}
  >
    <LogoIcon size={30} />
    {azienda && (
      <div
        style={{
          marginLeft: 12,
          paddingLeft: 12,
          borderLeft: `1px solid ${LT.n150}`,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          fontSize: 12,
          color: LT.n500,
          fontWeight: 500,
        }}
      >
        {azienda}
      </div>
    )}
    {titolo && (
      <div style={{ marginLeft: 20, fontSize: 16, fontWeight: 600, color: LT.n800 }}>{titolo}</div>
    )}
    <div style={{ flex: 1 }} />
    {children}
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 16 }}>
      {onBell && (
        <button
          onClick={onBell}
          style={{
            width: 36,
            height: 36,
            borderRadius: 999,
            background: LT.n100,
            border: 'none',
            cursor: 'pointer',
            color: LT.n600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label="Notifiche"
        >
          <Ico d={I.bell} size={18} />
        </button>
      )}
      {utente && (
        <div
          onClick={onProfilo}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            paddingLeft: 12,
            borderLeft: `1px solid ${LT.n150}`,
            height: 36,
            cursor: onProfilo ? 'pointer' : 'default',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              background: LT.grad,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {iniziali(utente)}
          </div>
          <div style={{ fontSize: 13, color: LT.n700, fontWeight: 600 }}>{utente}</div>
        </div>
      )}
    </div>
  </div>
);

// ─────── SideBar (shell desktop) ─────────────────────────────────────────────
// La sidebar è data-driven: l'utilizzatore passa `items` e `attiva` + handler
// `onSezione`. Compatibile 1:1 con la state machine `sezione` di App.js.

export const SideBar = ({ items = [], attiva, onSezione, width = 220 }) => (
  <div
    style={{
      width,
      background: '#fff',
      borderRight: `1px solid ${LT.n150}`,
      padding: '16px 10px',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      flexShrink: 0,
      overflowY: 'auto',
    }}
  >
    {items.map(it => {
      const on = it.id === attiva;
      return (
        <div
          key={it.id}
          onClick={() => onSezione && onSezione(it.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            padding: '9px 12px',
            borderRadius: LT.r.md,
            fontSize: 13,
            fontWeight: on ? 600 : 500,
            background: on ? LT.bluSoft : 'transparent',
            color: on ? LT.blu : LT.n600,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {it.icon && <Ico d={I[it.icon]} size={17} />}
          <span style={{ flex: 1 }}>{it.label}</span>
          {it.badge != null && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 999,
                background: on ? LT.blu : LT.n200,
                color: on ? '#fff' : LT.n600,
              }}
            >
              {it.badge}
            </span>
          )}
        </div>
      );
    })}
  </div>
);
